import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, Checkbox, FormControlLabel, Button } from '@mui/material';
import { Address, AddressCreationAttributes } from '../../models/Address';

interface AddressFormProps {
  addressToEdit?: Address;
  onSubmit: (data: AddressCreationAttributes) => Promise<void>;
  onCancel: () => void;
}

const provinces = [
  'dolnośląskie',
  'kujawsko-pomorskie',
  'lubelskie',
  'lubuskie',
  'łódzkie',
  'małopolskie',
  'mazowieckie',
  'opolskie',
  'podkarpackie',
  'podlaskie',
  'pomorskie',
  'śląskie',
  'świętokrzyskie',
  'warmińsko-mazurskie',
  'wielkopolskie',
  'zachodniopomorskie',
];

const AddressForm: React.FC<AddressFormProps> = ({
  addressToEdit,
  onSubmit,
  onCancel,
}) => {
  const isEditing = !!addressToEdit;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<AddressCreationAttributes>({
    defaultValues: addressToEdit || {
      name: '',
      street: '',
      buildingNumber: '',
      apartmentNumber: '',
      city: '',
      postalCode: '',
      province: '',
      phone: '',
      isDefault: false,
    },
  });

  // Własna walidacja formularza
  const validateForm = (data: AddressCreationAttributes) => {
    const errors: Record<string, string> = {};

    // Walidacja nazwy
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Nazwa adresu jest wymagana';
    }

    // Walidacja ulicy
    if (!data.street || data.street.trim() === '') {
      errors.street = 'Ulica jest wymagana';
    }

    // Walidacja numeru budynku
    if (!data.buildingNumber || data.buildingNumber.trim() === '') {
      errors.buildingNumber = 'Numer budynku jest wymagany';
    }

    // Walidacja miasta
    if (!data.city || data.city.trim() === '') {
      errors.city = 'Miasto jest wymagane';
    }

    // Walidacja kodu pocztowego
    if (!data.postalCode || data.postalCode.trim() === '') {
      errors.postalCode = 'Kod pocztowy jest wymagany';
    } else if (!/^\d{2}-\d{3}$/.test(data.postalCode)) {
      errors.postalCode = 'Kod pocztowy musi być w formacie XX-XXX';
    }

    // Walidacja województwa
    if (!data.province || data.province.trim() === '') {
      errors.province = 'Województwo jest wymagane';
    }

    // Walidacja telefonu (opcjonalnie)
    if (data.phone && !/^(\+48|48)?[0-9]{9}$/.test(data.phone)) {
      errors.phone = 'Nieprawidłowy format numeru telefonu';
    }

    return Object.keys(errors).length === 0 ? null : errors;
  };

  const handleFormSubmit = async (data: AddressCreationAttributes) => {
    // Przeprowadzamy własną walidację
    const validationErrors = validateForm(data);
    if (validationErrors) {
      // Ustawiamy błędy w formularzu
      for (const [field, message] of Object.entries(validationErrors)) {
        setValue(
          field as keyof AddressCreationAttributes,
          data[field as keyof AddressCreationAttributes],
          {
            shouldValidate: true,
            shouldDirty: true,
          },
        );
        // Ten błąd zostanie obsłużony przez formState.errors
      }
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6"
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          {isEditing ? 'Edytuj adres' : 'Dodaj nowy adres'}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 rounded-full p-1 focus:outline-none"
        >
          <CloseIcon />
        </button>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nazwa adresu */}
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Nazwa adresu jest wymagana',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nazwa adresu (np. Dom, Praca)"
                variant="outlined"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Ulica */}
          <Controller
            name="street"
            control={control}
            rules={{
              required: 'Ulica jest wymagana',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Ulica"
                variant="outlined"
                fullWidth
                error={!!errors.street}
                helperText={errors.street?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Numer budynku */}
          <Controller
            name="buildingNumber"
            control={control}
            rules={{
              required: 'Numer budynku jest wymagany',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Numer budynku"
                variant="outlined"
                fullWidth
                error={!!errors.buildingNumber}
                helperText={errors.buildingNumber?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Numer mieszkania */}
          <Controller
            name="apartmentNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Numer mieszkania (opcjonalnie)"
                variant="outlined"
                fullWidth
                error={!!errors.apartmentNumber}
                helperText={errors.apartmentNumber?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Miasto */}
          <Controller
            name="city"
            control={control}
            rules={{
              required: 'Miasto jest wymagane',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Miasto"
                variant="outlined"
                fullWidth
                error={!!errors.city}
                helperText={errors.city?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Kod pocztowy */}
          <Controller
            name="postalCode"
            control={control}
            rules={{
              required: 'Kod pocztowy jest wymagany',
              pattern: {
                value: /^\d{2}-\d{3}$/,
                message: 'Kod pocztowy musi być w formacie XX-XXX',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Kod pocztowy (XX-XXX)"
                variant="outlined"
                fullWidth
                error={!!errors.postalCode}
                helperText={errors.postalCode?.message}
                InputLabelProps={{ shrink: true }}
                placeholder="00-000"
              />
            )}
          />

          {/* Województwo */}
          <Controller
            name="province"
            control={control}
            rules={{
              required: 'Województwo jest wymagane',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Województwo"
                variant="outlined"
                fullWidth
                error={!!errors.province}
                helperText={errors.province?.message}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="" disabled>
                  Wybierz województwo
                </option>
                {provinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </TextField>
            )}
          />

          {/* Telefon */}
          <Controller
            name="phone"
            control={control}
            rules={{
              pattern: {
                value: /^(\+48|48)?[0-9]{9}$/,
                message: 'Nieprawidłowy format numeru telefonu',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Numer telefonu (opcjonalnie)"
                variant="outlined"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
                InputLabelProps={{ shrink: true }}
                placeholder="+48123456789"
              />
            )}
          />
        </div>

        {/* Adres domyślny */}
        <div>
          <Controller
            name="isDefault"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    {...field}
                    color="primary"
                  />
                }
                label="Ustaw jako adres domyślny"
              />
            )}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-3">
          <motion.button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Anuluj
          </motion.button>
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
            whileHover={{ scale: 1.03, backgroundColor: '#2563EB' }}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            {isSubmitting
              ? 'Zapisywanie...'
              : isEditing
                ? 'Zapisz zmiany'
                : 'Dodaj adres'}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default AddressForm;
